<template>
  <div class="mi-site mi-site--page">
    <a class="mi-skip" href="#main">Skip to content</a>
    <MarketingHeader />

    <main id="main" class="mi-status">
      <div class="mi-shell mi-status-hero">
        <p class="mi-status-kicker">MLS Ingest Status</p>
        <div class="mi-status-head">
          <h1>System status</h1>
          <router-link to="/contact" class="mi-ghost">Get notified</router-link>
        </div>
      </div>

      <div class="mi-shell">
        <div class="mi-status-banner" :class="'is-' + overall">
          <span class="mi-status-dot" aria-hidden="true"></span>
          {{ description }}
        </div>
        <p v-if="updatedLabel" class="mi-status-updated">Updated {{ updatedLabel }}</p>
      </div>

      <div class="mi-shell mi-status-block">
        <p class="mi-status-lead">Uptime over the past 90 days.</p>

        <section
          v-for="group in groups"
          :key="group.id"
          class="mi-status-group"
        >
          <h2 v-if="group.label">{{ group.label }}</h2>
          <article
            v-for="item in group.items"
            :key="item.id"
            class="mi-status-row"
          >
            <header>
              <h3>{{ item.name }}</h3>
              <span :class="'mi-status-pill is-' + item.status">{{ labelFor(item.status) }}</span>
            </header>
            <div class="mi-upbar" @mouseleave="tip = null">
              <i
                v-for="day in item.days"
                :key="day.date"
                :class="'is-' + day.status"
                :title="day.date"
                @mouseenter="showTip($event, item, day)"
              />
            </div>
            <p class="mi-upbar-meta">
              <span>90 days ago</span>
              <span>{{ uptimeLabel(item.uptime) }}</span>
              <span>Today</span>
            </p>
          </article>
        </section>

        <ul class="mi-status-legend">
          <li><i class="is-operational"></i> Operational</li>
          <li><i class="is-degraded"></i> Degraded performance</li>
          <li><i class="is-partial"></i> Partial outage</li>
          <li><i class="is-major"></i> Major outage</li>
          <li><i class="is-none"></i> No data</li>
        </ul>
      </div>

      <div
        v-if="tip"
        class="mi-status-tip"
        :style="tip.style"
      >
        <strong>{{ tip.title }}</strong>
        <span>{{ tip.body }}</span>
      </div>

      <div class="mi-shell mi-status-incidents">
        <h2>Past Incidents</h2>
        <div
          v-for="day in incidentDays"
          :key="day.date"
          class="mi-status-iday"
        >
          <h3>{{ formatDay(day.date) }}</h3>
          <p v-if="!day.incidents.length">No incidents reported.</p>
          <article v-for="inc in day.incidents" :key="inc.id || inc.name">
            <h4>{{ inc.name }}</h4>
            <p>{{ inc.body || labelFor(inc.impact) }}</p>
          </article>
        </div>
      </div>
    </main>

    <MarketingFooter />
  </div>
</template>

<script>
import MarketingHeader from './MarketingHeader'
import MarketingFooter from './MarketingFooter'
import '../assets/marketing.css'

const GROUPS = [
  { id: 'core', label: null },
]

export default {
  name: 'StatusPage',
  components: { MarketingHeader, MarketingFooter },
  data() {
    return {
      overall: 'operational',
      description: 'Checking systems…',
      updatedAt: null,
      components: [],
      incidentDays: [],
      tip: null,
      timer: null,
    }
  },
  computed: {
    updatedLabel() {
      if (!this.updatedAt) return ''
      try {
        return new Date(this.updatedAt).toLocaleString()
      } catch (err) {
        return this.updatedAt
      }
    },
    groups() {
      return GROUPS.map(group => ({
        id: group.id,
        label: group.label,
        items: this.components.filter(item => item.group === group.id),
      })).filter(group => group.items.length)
    },
  },
  mounted() {
    this.load()
    this.timer = setInterval(this.load, 60000)
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    async load() {
      try {
        const res = await fetch('/api/public/status')
        const data = await res.json()
        if (!data || !data.ok) throw new Error('bad status')
        this.overall = data.overall || 'operational'
        this.description = data.description || 'All Systems Operational'
        this.updatedAt = data.updatedAt
        this.components = data.components || []
        this.incidentDays = data.incidentDays || []
      } catch (err) {
        this.overall = 'major'
        this.description = 'Unable to load status'
        this.components = []
        this.incidentDays = []
      }
    },
    labelFor(status) {
      if (status === 'operational') return 'Operational'
      if (status === 'degraded') return 'Degraded Performance'
      if (status === 'partial') return 'Partial Outage'
      if (status === 'major') return 'Major Outage'
      if (status === 'maintenance') return 'Maintenance'
      return 'No data'
    },
    uptimeLabel(pct) {
      if (pct == null) return 'Collecting uptime'
      return pct.toFixed(1) + '% uptime'
    },
    formatDay(date) {
      const d = new Date(date + 'T12:00:00Z')
      return d.toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    },
    showTip(event, item, day) {
      const box = event.target.getBoundingClientRect()
      this.tip = {
        title: this.formatDay(day.date),
        body: item.name + ' — ' + this.labelFor(day.status).toLowerCase() + '.',
        style: {
          left: Math.round(box.left + box.width / 2) + 'px',
          top: Math.round(box.top) + 'px',
        },
      }
    },
  },
}
</script>
